import { FormHelperText, LinearProgress } from '@material-ui/core';
import { CSSProperties } from '@material-ui/styles';
import React, { FC, memo, useMemo } from 'react';
import { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';
import styled from 'styled-components';
import { viewTexts } from '../../constants/view-texts';
import { MuiButton } from './form-element';

const vt = viewTexts.pages.dndArea;

const Dropzone = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 2px;
  border-color: #eeeeee;
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
  min-height: 141px;
`;

const buttonHeightPx = 36.5;

const DummyButtonSpace = styled.div`
  height: ${buttonHeightPx}px;
`;

const LinearProgressWrapper = styled.div`
  padding: 10px;
`;

const ButtonWrapper = styled.div`
  height: ${buttonHeightPx}px;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const DropzoneMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DropzoneMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5px;
`;

const ErrorMessageWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

interface Props {
  dropzoneRootProps: DropzoneRootProps;
  dropzoneInputProps: DropzoneInputProps;
  isDragActive: boolean;
  errorMessage: string;
  isUploading: boolean;
  uploadPercentage: number;
  cancelUploading: () => void;
}

export const DndFileUploadAreaView: FC<Props> = memo(
  ({
    dropzoneRootProps,
    dropzoneInputProps,
    isDragActive,
    errorMessage,
    isUploading,
    uploadPercentage,
    cancelUploading
  }: Readonly<Props>) => {
    const focusedStyle = useMemo<CSSProperties>(
      () => (isDragActive && !isUploading ? { borderColor: '#2196f3' } : {}),
      [isDragActive, isUploading]
    );

    const pointerStyle = useMemo<CSSProperties>(
      () => (isUploading ? { cursor: 'not-allowed' } : { cursor: 'pointer' }),
      [isUploading]
    );

    const dropzoneStyle = useMemo<CSSProperties>(
      () => ({ ...focusedStyle, ...pointerStyle }),
      [focusedStyle, pointerStyle]
    );

    return (
      <div>
        <Dropzone {...dropzoneRootProps} style={dropzoneStyle}>
          {isUploading ? (
            <>
              <DummyButtonSpace />
              <LinearProgressWrapper>
                <LinearProgress
                  variant='determinate'
                  value={uploadPercentage}
                />
              </LinearProgressWrapper>
              <ButtonWrapper>
                <MuiButton
                  label={viewTexts.others.cancel}
                  color='default'
                  variant='text'
                  onClick={cancelUploading}
                />
              </ButtonWrapper>
            </>
          ) : (
            <>
              <input {...dropzoneInputProps} />
              <DropzoneMessageWrapper>
                {isDragActive ? (
                  <DropzoneMessage>{vt.dropHereMessage}</DropzoneMessage>
                ) : (
                  <DropzoneMessage>
                    <div>{vt.dropFileMessage}</div>
                    <div>{vt.availableFileTypeMessage}</div>
                  </DropzoneMessage>
                )}
              </DropzoneMessageWrapper>
            </>
          )}
        </Dropzone>
        <ErrorMessageWrapper>
          <FormHelperText error={true}>{errorMessage}</FormHelperText>
        </ErrorMessageWrapper>
      </div>
    );
  }
);

DndFileUploadAreaView.displayName = 'DndFileUploadAreaView';
